import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";

type Fetcher = (params: Record<string, any>) => Promise<any>;

interface Options {
  /** api/*Api.tsx içindeki get*List fonksiyonu */
  fetcher: Fetcher;
  /** Arama ve filtre dışındaki sabit parametreler */
  baseParams?: Record<string, any>;
  initialLimit?: number;
  initialSort?: string;
  /** Arama kutusu için gecikme (ms). Her tuşta istek atılmasını engeller. */
  searchDebounce?: number;
}

/**
 * Sayfalanan liste tablolarının ortak state'i: sayfa, sayfa boyutu, sıralama,
 * debounce'lu arama ve serbest filtreler.
 *
 * Filtre/arama değişiminde sayfayı 1'e almak burada tek yerde yapılır — daha önce
 * her tabloda `setPage(1)` elle tekrarlanıyordu.
 */
export const useTableQuery = ({
  fetcher,
  baseParams,
  initialLimit = 10,
  initialSort = "asc",
  searchDebounce = 300,
}: Options) => {
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(initialLimit);
  const [sort, setSortState] = useState(initialSort);
  const [search, setSearchState] = useState("");
  const [filters, setFiltersState] = useState<Record<string, any>>({});

  const [debouncedSearch] = useDebouncedValue(search, searchDebounce);

  const [rows, setRows] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // baseParams çoğu çağrı yerinde inline obje olarak veriliyor; referansı sabitlemezsek
  // her render'da yeni fetch tetiklenir.
  const baseParamsKey = JSON.stringify(baseParams ?? {});
  const stableBaseParams = useMemo(
    () => JSON.parse(baseParamsKey),
    [baseParamsKey]
  );

  const filtersKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data }: any = await fetcher({
        ...stableBaseParams,
        ...JSON.parse(filtersKey),
        pageNumber: page,
        limit,
        sort,
        name: debouncedSearch || undefined,
      });
      setRows(data?.data || []);
      setMeta(data?.meta || {});
      setTotalPages(data?.meta?.totalPages || 1);
    } catch (error) {
      console.error("Tablo verisi alınamadı:", error);
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [fetcher, stableBaseParams, filtersKey, page, limit, sort, debouncedSearch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const setSearch = useCallback((value: string) => {
    setPage(1);
    setSearchState(value);
  }, []);

  /** Tek bir filtreyi günceller; undefined vermek filtreyi kaldırır. */
  const setFilter = useCallback((key: string, value: any) => {
    setPage(1);
    setFiltersState((prev) => {
      const next = { ...prev };
      if (value === undefined || value === null || value === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }, []);

  const setLimit = useCallback((value: number) => {
    setPage(1);
    setLimitState(value);
  }, []);

  const setSort = useCallback((value: string) => {
    setPage(1);
    setSortState(value);
  }, []);

  return {
    rows,
    meta,
    loading,
    refetch,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    setSort,
    search,
    setSearch,
    filters,
    setFilter,
    totalPages,
  };
};
